import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link, useLocation } from "wouter";
import { Logo } from "./Logo";
import { ExternalLink } from "./ExternalLink";
import { SOPHIE_URL } from "@/lib/sophie-url";
import { SITE } from "@/lib/site-info";

type DropdownId = "what" | "industries" | "news";
type LinkId = DropdownId | "approach" | "sophie";

interface CapItem {
  num: string;
  name: string;
  desc: string;
  href: string;
  external?: boolean;
  span?: boolean;
}

const CAPS: CapItem[] = [
  {
    num: "01 — Platform",
    name: "Sophie",
    desc: "Managed contact and customer-service platform for regulated industries.",
    href: SOPHIE_URL,
    external: true,
  },
  {
    num: "02 — Intelligence",
    name: "AI + Data Integration",
    desc: "Operational insight from the data your systems already hold.",
    href: "/what-we-do/ai-data",
  },
  {
    num: "03 — Microsoft",
    name: "D365 Business Solutions",
    desc: "Dynamics 365 for contact centres and service operations.",
    href: "/what-we-do/d365",
  },
  {
    num: "04 — Automation",
    name: "Process Automation",
    desc: "Rules-based workflow automation at scale, with governance.",
    href: "/what-we-do/automation",
  },
  {
    num: "05 — Advisory",
    name: "UpliftX Framework",
    desc: "Map how work actually flows before you decide what to automate.",
    href: "/what-we-do/upliftx",
    span: true,
  },
];

interface IndustryItem {
  name: string;
  desc: string;
  accent: string;
  href: string;
}

const INDUSTRIES: IndustryItem[] = [
  {
    name: "Aged Care",
    desc: "Aged Care Act 2024, Support at Home, after-hours automation.",
    accent: "#D94F5C",
    href: "/industries/aged-care",
  },
  {
    name: "NDIS / Disability",
    desc: "Participant scheduling, plan tracking, QSC compliance.",
    accent: "#3A9B8E",
    href: "/industries/ndis",
  },
  {
    name: "Healthcare",
    desc: "Patient enquiry handling, clinical routing, Privacy Act workflows.",
    accent: "#2D7D9A",
    href: "/industries/healthcare",
  },
  {
    name: "Financial Services",
    desc: "CPS 230 aligned automation, claims, compliance workflows.",
    accent: "#7A5EA8",
    href: "/industries/financial-services",
  },
  {
    name: "Education",
    desc: "Scheduling, parent communications, compliance reporting.",
    accent: "#B07530",
    href: "/industries/education",
  },
  {
    name: "Government",
    desc: "Citizen enquiries, referrals, FOI-ready audit trails.",
    accent: "#5B6FA8",
    href: "/industries/government",
  },
];

interface NewsItem {
  tag: string;
  title: string;
  href: string;
}

const NEWS: NewsItem[] = [
  {
    tag: "Policy & Regulation",
    title: "The 2026–27 Budget, AI, and what it means for regulated service providers",
    href: "/news/budget-2026-27-ai-regulated-service-providers",
  },
  {
    tag: "Sophie Platform",
    title: "Sophie: service automation for regulated organisations",
    href: "/news/sophie-service-automation-for-regulated-organisations",
  },
  {
    tag: "Compliance",
    title: "Key Personnel compliance is getting harder to ignore",
    href: "/news/key-personnel-compliance-spreadsheets-not-the-answer",
  },
];

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="6"
      viewBox="0 0 10 6"
      style={{
        marginLeft: 4,
        marginBottom: 1,
        transition: "transform .22s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <polyline
        points="1,1 5,5 9,1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface MaybeLinkProps {
  href: string;
  external?: boolean;
  style?: CSSProperties;
  children: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function MaybeLink({ href, external, style, children, onMouseEnter, onMouseLeave }: MaybeLinkProps) {
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </a>
    );
  }
  return (
    <Link href={href} style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </Link>
  );
}

const FONT_SANS = "var(--font-body)";
const FONT_SERIF = "var(--font-heading)";

export function SiteHeader() {
  const [open, setOpen] = useState<DropdownId | null>(null);
  const [hovLink, setHovLink] = useState<LinkId | null>(null);
  const [hovItem, setHovItem] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [location] = useLocation();
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  const schedClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(null), 150);
  }, [cancelClose]);

  const openPanel = useCallback(
    (id: DropdownId) => {
      cancelClose();
      setOpen(id);
    },
    [cancelClose],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setOpen(null);
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", mobileOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [mobileOpen]);

  const isActive = (id: LinkId): boolean => {
    switch (id) {
      case "what":
        return location === "/what-we-do" || location.startsWith("/what-we-do/");
      case "industries":
        return location === "/industries" || location.startsWith("/industries/");
      case "approach":
        return location === "/approach";
      case "news":
        return location === "/news" || location.startsWith("/news/");
      default:
        return false;
    }
  };

  const linkStyle = (id: LinkId): CSSProperties => {
    const highlighted = hovLink === id || open === id || isActive(id);
    return {
      fontSize: 13.5,
      color: "#1B2A4A",
      opacity: highlighted ? 1 : 0.72,
      background: "none",
      border: "none",
      borderBottom: highlighted ? "2px solid #D94F5C" : "2px solid transparent",
      paddingBottom: 2,
      cursor: "pointer",
      fontFamily: "inherit",
      display: "inline-flex",
      alignItems: "center",
      textDecoration: "none",
      transition: "opacity .18s, border-color .18s",
      whiteSpace: "nowrap",
    };
  };

  const megaStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    background: "#fff",
    borderTop: "3px solid #D94F5C",
    borderBottom: "1px solid rgba(27,42,74,.1)",
    boxShadow: "0 8px 32px rgba(27,42,74,.12)",
    zIndex: 199,
  };

  const megaIn: CSSProperties = { maxWidth: 1280, margin: "0 auto", padding: "44px 52px 48px" };

  const label: CSSProperties = {
    fontSize: 9.5,
    fontWeight: 500,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: "#D94F5C",
    marginBottom: 24,
    display: "block",
    fontFamily: FONT_SANS,
  };

  const introTitle: CSSProperties = {
    fontFamily: FONT_SERIF,
    fontSize: 24,
    color: "#1B2A4A",
    lineHeight: 1.18,
    marginBottom: 12,
    fontStyle: "italic",
  };

  const introBody: CSSProperties = {
    fontSize: 14,
    color: "#5A5A5A",
    lineHeight: 1.68,
    marginBottom: 22,
    fontFamily: FONT_SANS,
  };

  const primaryLink: CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: "#D94F5C",
    textDecoration: "none",
    borderBottom: "1px solid rgba(217,79,92,.3)",
    paddingBottom: 3,
    fontFamily: FONT_SANS,
    display: "inline-block",
  };

  const capRow = (key: string, span?: boolean): CSSProperties => ({
    padding: "16px 24px",
    borderBottom: "1px solid rgba(27,42,74,.06)",
    background: hovItem === key ? "#F7F5F0" : "#fff",
    cursor: "pointer",
    textDecoration: "none",
    display: "block",
    transition: "background .15s",
    ...(span ? { gridColumn: "span 2" } : {}),
  });

  const capNum: CSSProperties = {
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#777",
    marginBottom: 4,
    fontFamily: FONT_SANS,
  };
  const capName: CSSProperties = {
    fontFamily: FONT_SERIF,
    fontSize: 16,
    color: "#1B2A4A",
    fontWeight: 500,
    marginBottom: 4,
  };
  const capDesc: CSSProperties = {
    fontSize: 12.5,
    color: "#5A5A5A",
    lineHeight: 1.5,
    fontFamily: FONT_SANS,
    margin: 0,
  };

  const indItem = (key: string): CSSProperties => ({
    padding: "16px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    textDecoration: "none",
    background: hovItem === key ? "#F7F5F0" : "#fff",
    borderBottom: "1px solid rgba(27,42,74,.05)",
    transition: "background .15s",
  });

  const indName: CSSProperties = {
    fontFamily: FONT_SERIF,
    fontSize: 16,
    color: "#1B2A4A",
    fontWeight: 500,
  };
  const indDesc: CSSProperties = {
    fontSize: 12.5,
    color: "#5A5A5A",
    lineHeight: 1.5,
    fontFamily: FONT_SANS,
    margin: 0,
  };

  const newsItemStyle = (key: string): CSSProperties => ({
    padding: "18px 22px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    textDecoration: "none",
    borderRight: "1px solid rgba(27,42,74,.06)",
    background: hovItem === key ? "#F7F5F0" : "#fff",
    transition: "background .15s",
  });

  const newsTag: CSSProperties = {
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#D94F5C",
    fontFamily: FONT_SANS,
  };
  const newsTitle: CSSProperties = {
    fontFamily: FONT_SERIF,
    fontSize: 15.5,
    color: "#1B2A4A",
    lineHeight: 1.28,
    fontWeight: 500,
    flex: 1,
  };
  const newsRead = (key: string): CSSProperties => ({
    fontSize: 12,
    fontWeight: 500,
    color: hovItem === key ? "#D94F5C" : "#777",
    marginTop: 4,
    fontFamily: FONT_SANS,
    transition: "color .15s",
  });

  const hov = (key: string) => ({
    onMouseEnter: () => setHovItem(key),
    onMouseLeave: () => setHovItem(null),
  });

  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <nav
        className="taidotech-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 200,
          background: "#fff",
          borderBottom: "1px solid rgba(27,42,74,.1)",
          fontFamily: FONT_SANS,
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "0 52px",
            height: 66,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 40,
          }}
        >
          {/* Logo */}
          <Link
            href="/"
            aria-label="Taidō — home"
            style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
          >
            <Logo variant="primary" height={33} priority />
          </Link>

          {/* Desktop links */}
          <ul
            className="taidotech-nav-links"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 32,
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            {/* What we do */}
            <li>
              <button
                type="button"
                style={linkStyle("what")}
                onMouseEnter={() => {
                  setHovLink("what");
                  openPanel("what");
                }}
                onMouseLeave={() => {
                  setHovLink(null);
                  schedClose();
                }}
                onFocus={() => openPanel("what")}
                aria-expanded={open === "what"}
                aria-haspopup="true"
              >
                What we do <Chevron open={open === "what"} />
              </button>
            </li>

            {/* Industries */}
            <li>
              <button
                type="button"
                style={linkStyle("industries")}
                onMouseEnter={() => {
                  setHovLink("industries");
                  openPanel("industries");
                }}
                onMouseLeave={() => {
                  setHovLink(null);
                  schedClose();
                }}
                onFocus={() => openPanel("industries")}
                aria-expanded={open === "industries"}
                aria-haspopup="true"
              >
                Industries <Chevron open={open === "industries"} />
              </button>
            </li>

            {/* Our approach — no dropdown */}
            <li>
              <Link
                href="/approach/"
                style={linkStyle("approach")}
                onMouseEnter={() => setHovLink("approach")}
                onMouseLeave={() => setHovLink(null)}
              >
                Our approach
              </Link>
            </li>

            {/* News */}
            <li>
              <button
                type="button"
                style={linkStyle("news")}
                onMouseEnter={() => {
                  setHovLink("news");
                  openPanel("news");
                }}
                onMouseLeave={() => {
                  setHovLink(null);
                  schedClose();
                }}
                onFocus={() => openPanel("news")}
                aria-expanded={open === "news"}
                aria-haspopup="true"
              >
                News <Chevron open={open === "news"} />
              </button>
            </li>

            {/* Sophie — external */}
            <li>
              <ExternalLink
                href={SOPHIE_URL}
                style={{ ...linkStyle("sophie"), gap: 3 }}
                onMouseEnter={() => setHovLink("sophie")}
                onMouseLeave={() => setHovLink(null)}
              >
                Sophie
              </ExternalLink>
            </li>

            {/* Contact CTA */}
            <li>
              <Link
                href="/contact/"
                style={{
                  background: "#D94F5C",
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 500,
                  padding: "10px 22px",
                  borderRadius: 2,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Contact us
              </Link>
            </li>
          </ul>

          {/* Mobile toggle */}
          <button
            type="button"
            className="taidotech-nav-mobile"
            style={{
              display: "none",
              background: "none",
              border: "none",
              color: "#1B2A4A",
              padding: 8,
              cursor: "pointer",
            }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Dropdown panels */}
        <div style={{ position: "relative", height: 0 }}>
          {/* WHAT WE DO */}
          {open === "what" && (
            <div
              style={megaStyle}
              onMouseEnter={cancelClose}
              onMouseLeave={schedClose}
              role="region"
              aria-label="What we do"
            >
              <div style={megaIn}>
                <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 64 }}>
                  <div>
                    <span style={label}>What we do</span>
                    <div style={introTitle}>Five capabilities. One partner.</div>
                    <p style={introBody}>
                      We build, deploy, and operate — so clients focus on the people they serve,
                      not the infrastructure behind it.
                    </p>
                    <Link href="/what-we-do" style={primaryLink}>
                      What we do — overview →
                    </Link>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      borderLeft: "1px solid rgba(27,42,74,.08)",
                    }}
                  >
                    {CAPS.map((c) => (
                      <MaybeLink
                        key={c.name}
                        href={c.href}
                        external={c.external}
                        style={capRow(c.name, c.span)}
                        onMouseEnter={hov(c.name).onMouseEnter}
                        onMouseLeave={hov(c.name).onMouseLeave}
                      >
                        <div style={capNum}>{c.num}</div>
                        <div style={capName}>{c.name}</div>
                        <p style={capDesc}>{c.desc}</p>
                      </MaybeLink>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* INDUSTRIES */}
          {open === "industries" && (
            <div
              style={megaStyle}
              onMouseEnter={cancelClose}
              onMouseLeave={schedClose}
              role="region"
              aria-label="Industries"
            >
              <div style={megaIn}>
                <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 64 }}>
                  <div>
                    <span style={label}>Industries</span>
                    <div style={introTitle}>
                      Regulated environments are where we work best.
                    </div>
                    <p style={introBody}>
                      Deep sector knowledge — and open to any organisation where governance
                      matters.
                    </p>
                    <Link href="/industries" style={primaryLink}>
                      All industries →
                    </Link>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3,1fr)",
                      borderLeft: "1px solid rgba(27,42,74,.08)",
                    }}
                  >
                    {[0, 1, 2].map((col) => (
                      <div
                        key={col}
                        style={{
                          borderRight: col < 2 ? "1px solid rgba(27,42,74,.06)" : "none",
                        }}
                      >
                        {[col, col + 3].map((i) => {
                          const ind = INDUSTRIES[i];
                          return (
                            <Link
                              key={ind.name}
                              href={ind.href}
                              style={indItem(ind.name)}
                              onMouseEnter={hov(ind.name).onMouseEnter}
                              onMouseLeave={hov(ind.name).onMouseLeave}
                            >
                              <div
                                style={{
                                  height: 2,
                                  width: 28,
                                  borderRadius: 1,
                                  background: ind.accent,
                                  marginBottom: 6,
                                }}
                              />
                              <div style={indName}>{ind.name}</div>
                              <p style={indDesc}>{ind.desc}</p>
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NEWS */}
          {open === "news" && (
            <div
              style={megaStyle}
              onMouseEnter={cancelClose}
              onMouseLeave={schedClose}
              role="region"
              aria-label="News"
            >
              <div style={megaIn}>
                <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 64 }}>
                  <div>
                    <span style={label}>Latest thinking</span>
                    <div style={introTitle}>Practical writing on AI and compliance.</div>
                    <Link
                      href="/news/"
                      style={{ ...primaryLink, marginTop: 12, display: "inline-block" }}
                    >
                      View all articles →
                    </Link>
                  </div>
                  <div
                    style={{
                      borderLeft: "1px solid rgba(27,42,74,.08)",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3,1fr)",
                      }}
                    >
                      {NEWS.map((n) => (
                        <Link
                          key={n.href}
                          href={n.href}
                          style={newsItemStyle(n.href)}
                          onMouseEnter={hov(n.href).onMouseEnter}
                          onMouseLeave={hov(n.href).onMouseLeave}
                        >
                          <div style={newsTag}>{n.tag}</div>
                          <div style={newsTitle}>{n.title}</div>
                          <span style={newsRead(n.href)}>Read article →</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`mobile-panel-overlay${mobileOpen ? " open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`mobile-panel${mobileOpen ? " open" : ""}`}
        aria-label="Mobile menu"
        inert={!mobileOpen}
      >
        <div className="mobile-panel-header">
          <button
            type="button"
            className="mobile-panel-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
        <div className="mobile-nav">
          <div className={`mobile-section${openSections.wwd ? " open" : ""}`}>
            <button
              type="button"
              onClick={() => toggleSection("wwd")}
              aria-expanded={!!openSections.wwd}
            >
              What we do
              <span aria-hidden="true">{openSections.wwd ? "−" : "+"}</span>
            </button>
            <div className="children">
              <Link href="/what-we-do/">Overview</Link>
              <ExternalLink href={SOPHIE_URL}>Sophie Platform</ExternalLink>
              <Link href="/what-we-do/ai-data/">AI + Data</Link>
              <Link href="/what-we-do/d365/">D365 Solutions</Link>
              <Link href="/what-we-do/automation/">Process Automation</Link>
              <Link href="/what-we-do/upliftx/">UpliftX Advisory</Link>
            </div>
          </div>
          <div className={`mobile-section${openSections.ind ? " open" : ""}`}>
            <button
              type="button"
              onClick={() => toggleSection("ind")}
              aria-expanded={!!openSections.ind}
            >
              Industries
              <span aria-hidden="true">{openSections.ind ? "−" : "+"}</span>
            </button>
            <div className="children">
              <Link href="/industries/">Overview</Link>
              {INDUSTRIES.map((i) => (
                <Link key={i.href} href={i.href}>
                  {i.name}
                </Link>
              ))}
            </div>
          </div>
          <Link href="/approach/" className="mobile-link">
            Our approach
          </Link>
          <Link href="/news/" className="mobile-link">
            News
          </Link>
          <Link href="/about/" className="mobile-link">
            About
          </Link>
          <ExternalLink href={SOPHIE_URL} className="mobile-link">
            Sophie
          </ExternalLink>
        </div>
        <div className="mobile-cta">
          <Link href="/contact/" className="btn btn-primary btn-block">
            Contact us
          </Link>
        </div>
        <div className="mobile-contact">
          <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
          <a href={`tel:${SITE.phoneTel}`}>{SITE.phone}</a>
        </div>
      </aside>
    </>
  );
}
